'use client'

import Link from 'next/link'
import { MC, SERIF } from '@/lib/mycio/theme'
import { useMyCIOLanguage } from '@/lib/mycio/MyCIOLanguageContext'
import { getMyCIOYourDataCopy } from '@/lib/mycio/translations'
import { INSTANT_PREFILL_EVENT, SAMPLE_SENTENCE } from '@/components/mycio/marketing/InstantHero'

/**
 * "Your numbers stay yours" — the data-trust section (the structural answer to "I don't want to type in
 * my real finances"): no account linking exists, rough numbers are first-class, everything is listed and
 * deletable, sharing is band-only. Four pillars in the capabilities-grid grammar + two CTAs:
 *  - "See a sample plan first" runs the labeled fictional household through the instant Value Map on this
 *    page (dispatches INSTANT_PREFILL_EVENT and scrolls to #try) — the zero-data taste. Only offered when
 *    the instant section is live (`showInstant`), otherwise the try-CTA points at sign-up.
 *  - The deep dive lives at /your-data.
 * COMPLIANCE: copy reuses the house phrasing ("holds no money, places no trades, and needs no brokerage",
 * "band only — never your amounts"); computed-not-advice; no invented security claims.
 */
export default function YourDataSection({ showInstant = false }: { showInstant?: boolean }) {
  const { language } = useMyCIOLanguage()
  const c = getMyCIOYourDataCopy(language)

  const runSample = () => {
    window.dispatchEvent(new CustomEvent<string>(INSTANT_PREFILL_EVENT, { detail: SAMPLE_SENTENCE }))
    document.getElementById('try')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const ctaStyle: React.CSSProperties = {
    display: 'inline-block', fontSize: 15.5, fontWeight: 600, color: MC.bg, background: MC.forest,
    border: 'none', padding: '14px 26px', borderRadius: 999, textDecoration: 'none', cursor: 'pointer',
  }
  const ctaGhostStyle: React.CSSProperties = {
    display: 'inline-block', fontSize: 15.5, fontWeight: 600, color: MC.forest, background: MC.surface,
    border: `1px solid ${MC.forest}`, padding: '13px 24px', borderRadius: 999, textDecoration: 'none', cursor: 'pointer',
  }

  return (
    <section id="your-data" className="mc-container mc-pad-section" style={{ maxWidth: 1180, margin: '0 auto', padding: '112px 32px' }}>
      <p style={{ fontSize: 12.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: MC.faint, fontWeight: 600, margin: '0 0 18px' }} data-reveal>
        {c.eyebrow}
      </p>
      <h2 className="mc-h2" style={{ fontFamily: SERIF, fontWeight: 400, fontSize: 46, lineHeight: 1.08, letterSpacing: '-0.02em', textWrap: 'balance', margin: '0 0 20px', maxWidth: 680 }} data-reveal>
        {c.titleBefore}<em style={{ fontStyle: 'italic', color: MC.forest }}>{c.titleEm}</em>{c.titleAfter}
      </h2>
      <p style={{ fontSize: 18, lineHeight: 1.6, color: MC.muted, margin: '0 0 56px', maxWidth: 620, textWrap: 'pretty' }} data-reveal>
        {c.body}
      </p>

      {/* The four pillars — the capabilities-grid treatment (hairline grid, tag · serif title · body). */}
      <div data-reveal style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 0, borderTop: '1px solid #D7CFBD', borderLeft: '1px solid #D7CFBD', marginBottom: 44 }}>
        {c.pillars.map((p) => (
          <div key={p.t} style={{ padding: '34px 38px', borderBottom: '1px solid #D7CFBD', borderRight: '1px solid #D7CFBD' }}>
            <p style={{ fontSize: 12, color: MC.clay, fontWeight: 600, margin: '0 0 12px', letterSpacing: '0.05em', textTransform: 'uppercase' }}>{p.tag}</p>
            <h3 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 24, margin: '0 0 10px', color: MC.ink }}>{p.t}</h3>
            <p style={{ fontSize: 15.5, lineHeight: 1.6, color: MC.muted, margin: 0, textWrap: 'pretty' }}>{p.p}</p>
          </div>
        ))}
      </div>

      <div data-reveal style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        {showInstant ? (
          <>
            {/* Runs the labeled fictional household through the instant card above — see it before typing anything. */}
            <button type="button" onClick={runSample} data-testid="your-data-sample" className="mc-cta-primary" style={ctaStyle}>
              {c.sampleCta}
            </button>
            <a href="#try" style={ctaGhostStyle}>{c.tryCta}</a>
          </>
        ) : (
          <Link href="/sign-in#signup" className="mc-cta-primary" style={ctaStyle}>{c.tryCta}</Link>
        )}
        <Link href="/your-data" className="mc-link-underline" style={{ fontSize: 15, fontWeight: 600, color: MC.forest, textDecoration: 'none' }}>
          {c.moreLink}
        </Link>
        <p className="no-print" style={{ flexBasis: '100%', margin: '10px 0 0', fontSize: 13.5, color: MC.faint }}>
          {c.securityNote}
        </p>
      </div>
    </section>
  )
}
